import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { InputOTP, InputOTPGroup, InputOTPSlot } from '@/components/ui/input-otp';
import { Eye, EyeOff, Loader2, ArrowLeft, CheckCircle, XCircle } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const LOGO_URL = "https://customer-assets.emergentagent.com/job_readiness-check-27/artifacts/el8npnpt_CXOlogo.png";

const ForgotPasswordPage = () => {
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: Enter identifier, 2: Enter OTP, 3: New password
  const [loading, setLoading] = useState(false);
  const [identifier, setIdentifier] = useState('');
  const [email, setEmail] = useState('');
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  const requirements = [
    { label: 'At least 8 characters', met: password.length >= 8 },
    { label: 'At least 1 number', met: /\d/.test(password) },
    { label: 'At least 1 special character', met: /[!@#$%^&*(),.?":{}|<>]/.test(password) }
  ];

  const allRequirementsMet = requirements.every(r => r.met);
  const passwordsMatch = password === confirmPassword && confirmPassword.length > 0;

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!identifier.trim()) {
      toast.error('Please enter your username or email');
      return;
    }

    setLoading(true);
    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { identifier });
      // The API deliberately no longer returns the account's email address
      // (account-enumeration hardening), so show a generic confirmation.
      setEmail(response.data.email || 'your registered email address');
      setStep(2);
      toast.success('OTP sent to your registered email');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send OTP');
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async () => {
    if (otp.length !== 6) {
      toast.error('Please enter the complete OTP');
      return;
    }
    setStep(3);
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    
    if (!allRequirementsMet) {
      toast.error('Please meet all password requirements');
      return;
    }
    
    if (!passwordsMatch) {
      toast.error('Passwords do not match');
      return;
    }

    setLoading(true);
    try {
      // Send exactly what the user typed — the server resolves a username to
      // the stored work email. The client never builds a login domain.
      await axios.post(`${API}/auth/verify-otp`, {
        identifier,
        otp,
        new_password: password
      });
      
      toast.success('Password reset successful');
      navigate('/login');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex flex-col">
      {/* Header */}
      <header className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-center">
          <Link to="/" data-testid="header-logo">
            <img src={LOGO_URL} alt="CodeXero" className="h-10" />
          </Link>
        </div>
      </header>

      {/* Content */}
      <div className="flex-1 flex items-center justify-center py-12 px-6">
        <div className="w-full max-w-md">
          <Link 
            to="/login" 
            className="flex items-center gap-2 text-slate-500 hover:text-[#008080] transition-colors mb-6"
            data-testid="back-to-login"
          >
            <ArrowLeft className="h-4 w-4" />
            Back to Login
          </Link>

          <div className="bg-white border border-slate-200 rounded-sm p-8 shadow-[0_4px_24px_rgba(0,51,102,0.06)]">
            {step === 1 && (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="forgot-password-heading">
                    Forgot Password?
                  </h1>
                  <p className="text-slate-600">
                    Enter your username or email to receive a reset OTP
                  </p>
                </div>

                <form onSubmit={handleRequestOTP} className="space-y-6">
                  <div>
                    <Label htmlFor="identifier" className="text-[#003366] font-medium">Username or Email</Label>
                    <Input
                      id="identifier"
                      data-testid="identifier-input"
                      value={identifier}
                      onChange={(e) => setIdentifier(e.target.value)}
                      className="mt-1 rounded-sm"
                      placeholder="priya.sharma or priya@company.com"
                    />
                  </div>

                  <Button
                    type="submit"
                    disabled={loading}
                    data-testid="request-otp-btn"
                    className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm btn-hover"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Sending OTP...
                      </>
                    ) : (
                      'Send OTP'
                    )}
                  </Button>
                </form>
              </>
            )}

            {step === 2 && (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="verify-otp-heading">
                    Enter OTP
                  </h1>
                  <p className="text-slate-600">
                    We've sent a 6-digit OTP to <span className="font-semibold">{email}</span>
                  </p>
                  <p className="text-slate-500 text-sm mt-2">OTP is valid for 10 minutes</p>
                </div>

                <div className="flex justify-center mb-6">
                  <InputOTP maxLength={6} value={otp} onChange={setOtp} data-testid="otp-input">
                    <InputOTPGroup>
                      <InputOTPSlot index={0} />
                      <InputOTPSlot index={1} />
                      <InputOTPSlot index={2} />
                      <InputOTPSlot index={3} />
                      <InputOTPSlot index={4} />
                      <InputOTPSlot index={5} />
                    </InputOTPGroup>
                  </InputOTP>
                </div>

                <Button
                  onClick={handleVerifyOTP}
                  disabled={otp.length !== 6}
                  data-testid="verify-otp-btn"
                  className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm btn-hover"
                >
                  Verify OTP
                </Button>

                <button 
                  onClick={() => setStep(1)}
                  className="w-full text-center text-[#008080] hover:underline mt-4 text-sm"
                >
                  Didn't receive OTP? Try again
                </button>
              </>
            )}

            {step === 3 && (
              <>
                <div className="text-center mb-8">
                  <h1 className="text-2xl font-[800] text-[#003366] mb-2" data-testid="new-password-heading">
                    Set New Password
                  </h1>
                  <p className="text-slate-600">
                    Create a strong password for your account
                  </p>
                </div>

                <form onSubmit={handleResetPassword} className="space-y-6">
                  <div>
                    <Label htmlFor="password" className="text-[#003366] font-medium">New Password</Label>
                    <div className="relative mt-1">
                      <Input
                        id="password"
                        type={showPassword ? 'text' : 'password'}
                        data-testid="new-password-input"
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="rounded-sm pr-10"
                        placeholder="Enter new password"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                      >
                        {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                      </button>
                    </div>
                    
                    {password.length > 0 && (
                      <ul className="mt-2 space-y-1">
                        {requirements.map((req, i) => (
                          <li 
                            key={i} 
                            className={`flex items-center gap-2 text-sm ${req.met ? 'text-green-600' : 'text-slate-500'}`}
                          >
                            {req.met ? <CheckCircle className="h-4 w-4" /> : <XCircle className="h-4 w-4" />}
                            {req.label}
                          </li>
                        ))}
                      </ul>
                    )}
                  </div>

                  <div>
                    <Label htmlFor="confirmPassword" className="text-[#003366] font-medium">Confirm Password</Label>
                    <Input
                      id="confirmPassword"
                      type="password"
                      data-testid="confirm-password-input"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      className={`mt-1 rounded-sm ${
                        confirmPassword.length > 0 && !passwordsMatch ? 'border-red-500' : ''
                      }`}
                      placeholder="Confirm new password"
                    />
                    {confirmPassword.length > 0 && !passwordsMatch && (
                      <p className="text-red-500 text-sm mt-1">Passwords do not match</p>
                    )}
                  </div>

                  <Button
                    type="submit"
                    disabled={loading || !allRequirementsMet || !passwordsMatch}
                    data-testid="reset-password-btn"
                    className="w-full bg-[#003366] hover:bg-[#002244] text-white py-6 rounded-sm btn-hover disabled:opacity-50"
                  >
                    {loading ? (
                      <>
                        <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                        Resetting...
                      </>
                    ) : (
                      'Reset Password'
                    )}
                  </Button>
                </form>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Footer */}
      <footer className="bg-[#003366] text-white py-6">
        <div className="max-w-7xl mx-auto px-6 text-center">
          <p className="text-slate-400 text-sm">© CodeXero {new Date().getFullYear()}</p>
        </div>
      </footer>
    </div>
  );
};

export default ForgotPasswordPage;
